/**
 * DeviceCreatedModal
 *
 * Modal que aparece INMEDIATAMENTE tras crear un dispositivo POS.
 * Muestra el usuario y contraseña generados —listos para copiar— antes
 * de que el usuario abandone la pantalla.
 *
 * La contraseña se almacena cifrada en el servidor; esta es la ÚNICA vez
 * que se puede ver en texto plano.
 */

import { useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, Monitor, ShieldCheck } from "lucide-react";

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface DeviceCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceName: string;
  username: string;
  password: string;
  companyId: string;
  branchId: string;
  backendUrl: string;
  dbPassword: string;
  syncToken: string;
}

// ─── Componente ──────────────────────────────────────────────────────────────

export default function DeviceCreatedModal({
  isOpen,
  onClose,
  deviceName,
  username,
  password,
  companyId,
  branchId,
  backendUrl,
  dbPassword,
  syncToken,
}: DeviceCreatedModalProps) {
  const envText = [
    `REMOTE_SERVER_URL=${backendUrl || "(sin URL de backend asignada)"}`,
    `COMPANY_UUID=${companyId}`,
    `BRANCH_UUID=${branchId}`,
    `DEVICE_USERNAME=${username}`,
    `DEVICE_PASSWORD=${password}`,
    `DB_PASSWORD=${dbPassword || "(no disponible)"}`,
    `SYNC_LOCAL_API_TOKEN=${syncToken || "(no disponible)"}`,
  ].join("\n");

  const closeRef = useRef<HTMLButtonElement>(null);

  const handleDownload = () => {
    const json = JSON.stringify(
      {
        REMOTE_SERVER_URL:   backendUrl || "",
        COMPANY_UUID:        companyId,
        BRANCH_UUID:         branchId,
        DEVICE_USERNAME:     username,
        DEVICE_PASSWORD:     password,
        DB_PASSWORD:         dbPassword || "",
        SYNC_LOCAL_API_TOKEN: syncToken || "",
      },
      null,
      2,
    );
    const blob = new Blob([json], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `credentials_${companyId}_${branchId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-[520px] bg-slate-900 border-emerald-900/40 text-white"
        // Impedir cerrar haciendo clic fuera — el usuario debe leer las credenciales
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            </div>
            <DialogTitle className="text-white text-lg font-semibold leading-tight">
              Dispositivo creado
            </DialogTitle>
          </div>

          <DialogDescription asChild>
            <div className="space-y-1 text-sm text-white/60">
              <div className="flex items-center gap-2">
                <Monitor className="h-4 w-4 flex-shrink-0 text-white/40" />
                <span className="font-medium text-white/80 truncate">{deviceName}</span>
              </div>
              <p>
                Copia las credenciales antes de cerrar.{" "}
                <span className="text-amber-400 font-semibold">
                  La contraseña no se podrá recuperar
                </span>{" "}
                sin regenerarla.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Lienzo seleccionable — seleccionar con mouse y Ctrl+C */}
        <div className="space-y-1.5 py-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
            Selecciona el texto y copia con Ctrl+C / Cmd+C
          </p>
          <textarea
            readOnly
            value={envText}
            rows={7}
            className="w-full resize-none rounded-lg border border-white/15 bg-black/40 px-3 py-3 font-mono text-xs text-emerald-300 leading-relaxed focus:outline-none focus:border-white/30 cursor-text"
            onClick={(e) => (e.target as HTMLTextAreaElement).select()}
          />
        </div>

        {/* Aviso de seguridad */}
        <div className="flex items-start gap-2 rounded-xl border border-amber-500/25 bg-amber-500/8 px-3 py-2.5">
          <ShieldCheck className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300/80 leading-relaxed">
            Almacena estas credenciales en un lugar seguro. Esta ventana es la única
            oportunidad para ver la contraseña en texto claro.
          </p>
        </div>

        {/* Descargar */}
        <Button
          variant="outline"
          onClick={handleDownload}
          className="w-full border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
        >
          <Download className="h-4 w-4 mr-2" />
          Descargar credentials.json
        </Button>

        {/* Footer */}
        <DialogFooter>
          <Button
            ref={closeRef}
            onClick={onClose}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Ya guardé las credenciales
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
